import { useState } from 'react';
import { HelpCircle } from 'lucide-react';
import { Modal } from '@/components/ui/modal';
import { EditorField, EdSelect, EdInput } from './editor-field';
import type { DraftAction, LotSizing } from './editor-types';

const LOT_TYPES = ['FIXED', 'MARTINGALE', 'RISK_BASED', 'EQUITY_PCT'];

function LotSizingInfo() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="cursor-pointer">
        <HelpCircle size={11} className="text-ink-4 hover:text-orange transition-colors" />
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="Lot Sizing Methods">
        <div className="space-y-4 text-xs text-ink-2 leading-relaxed">
          <div>
            <h3 className="text-[11px] font-bold text-ink-1 mb-1">Fixed</h3>
            <p>Uses the same lot size for every trade regardless of account balance or conditions.</p>
            <div className="bg-surface rounded-lg px-3 py-2 mt-1.5">
              <p className="text-[10px] text-ink-3">Volume = <strong className="text-ink-2">0.10</strong> → every trade opens at 0.10 lots.</p>
            </div>
          </div>

          <div className="h-px bg-border" />

          <div>
            <h3 className="text-[11px] font-bold text-ink-1 mb-1">Risk Based</h3>
            <p>Calculates lot size so that each trade risks a fixed percentage of your equity. Position size adapts as your balance grows or shrinks.</p>
            <div className="bg-surface rounded-lg px-3 py-2 mt-1.5">
              <p className="text-[10px] text-ink-3">Risk % = <strong className="text-ink-2">2</strong>, Equity = $1,000 → each trade risks $20, and lot size is calculated accordingly.</p>
            </div>
          </div>

          <div className="h-px bg-border" />

          <div>
            <h3 className="text-[11px] font-bold text-ink-1 mb-1">Equity %</h3>
            <p>Sets lot size as a percentage of the bot's current equity. Similar to Risk Based but simpler — directly ties volume to equity.</p>
            <div className="bg-surface rounded-lg px-3 py-2 mt-1.5">
              <p className="text-[10px] text-ink-3">Equity % = <strong className="text-ink-2">5</strong>, Equity = $1,000 → lot size calculated from $50 notional.</p>
            </div>
          </div>

          <div className="h-px bg-border" />

          <div>
            <h3 className="text-[11px] font-bold text-orange mb-1">Martingale</h3>
            <p>Increases (or decreases) lot size with each consecutive grid level using a multiplier. Combined with grid strategies for progressive position sizing.</p>
            <div className="bg-surface rounded-lg px-3 py-2 mt-1.5 font-mono text-[10px] text-ink-3 space-y-0.5">
              <p>Base Risk = <strong className="text-ink-2">1</strong>, Multiplier = <strong className="text-ink-2">2.0</strong></p>
              <p>Level 1: 0.01 lots</p>
              <p>Level 2: 0.02 lots</p>
              <p>Level 3: 0.04 lots</p>
              <p>Level 4: 0.08 lots</p>
            </div>
            <p className="text-ink-3 mt-1.5">Multiplier &gt; 1 = classic martingale (lot increases). Multiplier &lt; 1 = reverse martingale (lot decreases). Multiplier = 1 = flat sizing.</p>
            <p className="text-red/80 mt-1.5 font-semibold">Warning: Classic martingale (multiplier &gt; 1) can lead to rapid capital depletion during extended losing streaks.</p>
          </div>
        </div>
      </Modal>
    </>
  );
}

function VolumeInfo() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="cursor-pointer">
        <HelpCircle size={11} className="text-ink-4 hover:text-orange transition-colors" />
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="Fixed Volume (Lots)">
        <div className="space-y-4 text-xs text-ink-2 leading-relaxed">
          <p>The exact lot size used for every trade. One standard lot = 100,000 units of the base currency.</p>

          <div className="h-px bg-border" />

          <div>
            <h3 className="text-[11px] font-bold text-ink-1 mb-1">Common Lot Sizes</h3>
            <div className="space-y-1.5">
              <div className="bg-surface rounded-lg px-3 py-2 flex items-center justify-between">
                <span className="text-[10px] font-bold text-ink-3">0.01</span>
                <span className="text-[10px] text-ink-4">Micro lot (1,000 units) — ~$0.10/pip for forex</span>
              </div>
              <div className="bg-surface rounded-lg px-3 py-2 flex items-center justify-between">
                <span className="text-[10px] font-bold text-ink-3">0.10</span>
                <span className="text-[10px] text-ink-4">Mini lot (10,000 units) — ~$1.00/pip for forex</span>
              </div>
              <div className="bg-surface rounded-lg px-3 py-2 flex items-center justify-between">
                <span className="text-[10px] font-bold text-ink-3">1.00</span>
                <span className="text-[10px] text-ink-4">Standard lot (100,000 units) — ~$10.00/pip for forex</span>
              </div>
            </div>
          </div>

          <p className="text-ink-4">For gold (XAUUSD), 0.01 lots ≈ $0.01/pip. For indices, pip values vary by contract specification.</p>
        </div>
      </Modal>
    </>
  );
}

function BaseRiskInfo() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="cursor-pointer">
        <HelpCircle size={11} className="text-ink-4 hover:text-orange transition-colors" />
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="Base Risk Factor">
        <div className="space-y-4 text-xs text-ink-2 leading-relaxed">
          <p>The starting lot size factor for the first grid level in a martingale sequence. Subsequent levels multiply this base by the multiplier.</p>

          <div className="bg-surface rounded-lg px-3 py-2">
            <span className="text-[10px] font-bold text-ink-3">Example</span>
            <p className="text-[10px] text-ink-3 mt-0.5">Base Risk = <strong className="text-ink-2">1</strong> → first level trades at the minimum lot (e.g., 0.01). Base Risk = <strong className="text-ink-2">2</strong> → first level trades at 2× minimum (e.g., 0.02).</p>
          </div>

          <p className="text-ink-4">Higher base risk = larger starting position and faster capital usage across grid levels. Start with <strong className="text-ink-3">1</strong> for conservative sizing.</p>
        </div>
      </Modal>
    </>
  );
}

function MultiplierInfo() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="cursor-pointer">
        <HelpCircle size={11} className="text-ink-4 hover:text-orange transition-colors" />
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="Martingale Multiplier">
        <div className="space-y-4 text-xs text-ink-2 leading-relaxed">
          <p>How much the lot size changes at each consecutive grid level.</p>

          <div className="space-y-1.5">
            <div className="bg-surface rounded-lg px-3 py-2">
              <span className="text-[10px] font-bold text-green">Multiplier &gt; 1 (Classic Martingale)</span>
              <p className="text-[10px] text-ink-4 mt-0.5">Lot increases each level. E.g., 2.0× → 0.01, 0.02, 0.04, 0.08...</p>
            </div>
            <div className="bg-surface rounded-lg px-3 py-2">
              <span className="text-[10px] font-bold text-amber">Multiplier &lt; 1 (Reverse Martingale)</span>
              <p className="text-[10px] text-ink-4 mt-0.5">Lot decreases each level. E.g., 0.5× → 0.08, 0.04, 0.02, 0.01...</p>
            </div>
            <div className="bg-surface rounded-lg px-3 py-2">
              <span className="text-[10px] font-bold text-ink-3">Multiplier = 1 (Flat)</span>
              <p className="text-[10px] text-ink-4 mt-0.5">Same lot at every level. E.g., 0.01, 0.01, 0.01, 0.01...</p>
            </div>
          </div>

          <div className="h-px bg-border" />

          <p className="text-red/80 font-semibold">Classic martingale (multiplier above 1) can rapidly deplete capital. A 2× multiplier with 10 levels requires 1,023× the base lot in total capital.</p>
        </div>
      </Modal>
    </>
  );
}

function RiskPctInfo() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="cursor-pointer">
        <HelpCircle size={11} className="text-ink-4 hover:text-orange transition-colors" />
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="Risk Percentage">
        <div className="space-y-4 text-xs text-ink-2 leading-relaxed">
          <p>The percentage of your bot's equity risked on each trade. The system calculates the appropriate lot size based on this risk amount and the instrument's pip value.</p>

          <div className="bg-surface rounded-lg px-3 py-2">
            <span className="text-[10px] font-bold text-ink-3">Example</span>
            <p className="text-[10px] text-ink-3 mt-0.5">Equity = $1,000, Risk = <strong className="text-ink-2">2%</strong> → risks $20 per trade. If each pip = $1, and SL is 20 pips, lot = 0.10.</p>
          </div>

          <div className="h-px bg-border" />

          <div>
            <h3 className="text-[11px] font-bold text-ink-1 mb-1">Suggested Values</h3>
            <div className="grid grid-cols-2 gap-1.5">
              <div className="bg-surface rounded-lg px-3 py-2">
                <span className="text-[10px] font-bold text-ink-3">Conservative</span>
                <p className="text-[10px] text-ink-4">0.5 – 1%</p>
              </div>
              <div className="bg-surface rounded-lg px-3 py-2">
                <span className="text-[10px] font-bold text-ink-3">Moderate</span>
                <p className="text-[10px] text-ink-4">1 – 2%</p>
              </div>
              <div className="bg-surface rounded-lg px-3 py-2">
                <span className="text-[10px] font-bold text-ink-3">Aggressive</span>
                <p className="text-[10px] text-ink-4">3 – 5%</p>
              </div>
              <div className="bg-surface rounded-lg px-3 py-2">
                <span className="text-[10px] font-bold text-ink-3">Max recommended</span>
                <p className="text-[10px] text-ink-4">5%</p>
              </div>
            </div>
          </div>

          <p className="text-ink-4">Position size automatically adjusts as equity changes — larger lots when winning, smaller when losing (anti-martingale effect).</p>
        </div>
      </Modal>
    </>
  );
}

function EquityPctInfo() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="cursor-pointer">
        <HelpCircle size={11} className="text-ink-4 hover:text-orange transition-colors" />
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="Equity Percentage">
        <div className="space-y-4 text-xs text-ink-2 leading-relaxed">
          <p>The percentage of your bot's current equity used to calculate the lot size. Unlike Risk Based, this directly converts a fraction of equity to notional trade value.</p>

          <div className="bg-surface rounded-lg px-3 py-2">
            <span className="text-[10px] font-bold text-ink-3">Example</span>
            <p className="text-[10px] text-ink-3 mt-0.5">Equity = $1,000, Equity % = <strong className="text-ink-2">5</strong> → uses $50 notional value to determine lot size.</p>
          </div>

          <p className="text-ink-4">Similar to Risk Based but simpler — no stop-loss distance is factored in. Good for strategies without explicit SL levels.</p>
        </div>
      </Modal>
    </>
  );
}

export function LotSizingEditor({
  lot,
  path,
  dispatch,
}: {
  lot: LotSizing;
  path: string;
  dispatch: React.Dispatch<DraftAction>;
}) {
  if (!lot) return null;

  return (
    <div>
      <EditorField label={
        <span className="flex items-center gap-1">
          Type
          <LotSizingInfo />
        </span>
      }>
        <EdSelect
          value={lot.type}
          onChange={(v) => dispatch({ type: 'CHANGE_LOT_TYPE', path, newType: v })}
          options={LOT_TYPES}
        />
      </EditorField>

      {lot.type === 'FIXED' && (
        <EditorField label={
          <span className="flex items-center gap-1">
            Volume
            <VolumeInfo />
          </span>
        }>
          <EdInput
            value={lot.volume}
            onChange={(v) => dispatch({ type: 'SET_PATH_NUM', path: `${path}.volume`, value: v })}
            step="any"
            min="0.01"
            small
          />
          <span className="text-[10px] text-ink-4">lots</span>
        </EditorField>
      )}

      {lot.type === 'MARTINGALE' && (
        <>
          <EditorField label={
            <span className="flex items-center gap-1">
              Base Risk
              <BaseRiskInfo />
            </span>
          }>
            <EdInput
              value={lot.baseLotRiskFactor}
              onChange={(v) => dispatch({ type: 'SET_PATH_NUM', path: `${path}.baseLotRiskFactor`, value: v })}
              step="any"
              small
            />
          </EditorField>
          <EditorField label={
            <span className="flex items-center gap-1">
              Multiplier
              <MultiplierInfo />
            </span>
          }>
            <EdInput
              value={lot.multiplier}
              onChange={(v) => dispatch({ type: 'SET_PATH_NUM', path: `${path}.multiplier`, value: v })}
              step="any"
              small
            />
            <span className="text-[10px] text-ink-4">&times;</span>
          </EditorField>
          <p
            className={`text-[9px] font-semibold -mt-1 mb-1 ${
              lot.multiplier > 1
                ? 'text-green'
                : lot.multiplier < 1
                  ? 'text-amber'
                  : 'text-ink-4'
            }`}
          >
            {lot.multiplier > 1
              ? 'Lot increases each level (martingale)'
              : lot.multiplier < 1
                ? 'Lot decreases each level (reverse martingale)'
                : 'Lot stays the same each level'}
          </p>
        </>
      )}

      {lot.type === 'RISK_BASED' && (
        <EditorField label={
          <span className="flex items-center gap-1">
            Risk %
            <RiskPctInfo />
          </span>
        }>
          <EdInput
            value={lot.riskPct}
            onChange={(v) => dispatch({ type: 'SET_PATH_NUM', path: `${path}.riskPct`, value: v })}
            step="0.1" min="0.1" max="100" small
          />
        </EditorField>
      )}

      {lot.type === 'EQUITY_PCT' && (
        <EditorField label={
          <span className="flex items-center gap-1">
            Equity %
            <EquityPctInfo />
          </span>
        }>
          <EdInput
            value={lot.pct}
            onChange={(v) => dispatch({ type: 'SET_PATH_NUM', path: `${path}.pct`, value: v })}
            step="0.1" min="0.1" max="100" small
          />
        </EditorField>
      )}
    </div>
  );
}
